/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';

import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { boxAlignmentInterpolations } from '../utils/UNSAFE_interpolations/boxalignment';
import type { BoxAlignmentProps } from '../utils/UNSAFE_interpolations/boxalignment';
import { flexboxInterpolations } from '../utils/UNSAFE_interpolations/flexbox';
import type { FlexboxProps } from '../utils/UNSAFE_interpolations/flexbox';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { classNames } from '../utils/UNSAFE_classNames';
import { baseStyles, marginZeroStyles } from './FlexStyles.css';

type StyleProps = DimensionProps & FlexboxProps & FlexitemProps & BoxAlignmentProps;

type Props = StyleProps & {
  children?: ComponentChildren;
  /**
   * @default true
   */
  hasZeroMargins?: boolean;
};

const interpolations = [
  ...Object.values(dimensionInterpolations),
  ...Object.values(flexboxInterpolations),
  ...Object.values(flexitemInterpolations),
  ...Object.values(boxAlignmentInterpolations)
];

const styleInterpolations = mergeInterpolations<Props>(interpolations);

export const Flex = ({ children, hasZeroMargins = true, ...props }: Props) => {
  const flexComponentClasses = classNames([baseStyles, hasZeroMargins && marginZeroStyles]);
  const { class: cls, ...styles } = styleInterpolations(props);
  return (
    <div class={`${flexComponentClasses} ${cls}`} style={styles}>
      {children}
    </div>
  );
}; 

